import { useState, useRef } from 'react';
import { playChunkViaAudioContext } from '../utils/audioutils';

/**
 * Custom hook for handling SSE (Server-Sent Events) messages
 * @param {Function} setMessages - Function to update messages state
 * @param {boolean} isAudioReady - Whether audio is ready to play
 * @param {Function} saveCurrentSession - Function to save the current session
 * @returns {Object} SSE message handling state and functions
 */
const useSSEMessageHandler = (setMessages, isAudioReady, saveCurrentSession) => {
  const [currentAIMessage, setCurrentAIMessage] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const currentAIMessageRef = useRef(''); // Create a ref to store the current message

  /**
   * Handle SSE message events
   * @param {Object} event - The SSE event
   */
  const handleSSEMessage = async (event) => {
    switch (event.type) {
      case 'connection_error':
      case 'connection_warning':
      case 'connection_restored':
      case 'connection_failed':
      case 'message_queued':
        // Handle connection status messages
        console.log(`Connection status: ${event.type}`, event.message);
        
        // Update the messages array with the connection status message
        setMessages(prev => {
          // Find the index of the "Typing..." message
          const typingIndex = prev.findIndex(msg => 
            msg.role === 'AI' && msg.content === 'Typing...'
          );
          
          if (typingIndex !== -1) {
            // Create a new array with the updated message
            const updatedMessages = [...prev];
            updatedMessages[typingIndex] = {
              ...updatedMessages[typingIndex],
              thinking: `${updatedMessages[typingIndex].thinking || ''}\n${event.message}`,
              showThinking: true // Always show thinking when there's a connection issue
            };
            
            return updatedMessages;
          }
          
          return prev;
        });
        break;
        
      case 'text_delta':
        // Handle case where content is missing or undefined
        const content = event.content || "";
        if (!event.content) {
          console.warn('text_delta event is missing content, using empty string');
        }
        
        // Update the ref immediately with the new content
        currentAIMessageRef.current = currentAIMessageRef.current + content;
        
        // Update the state for UI reactivity
        setCurrentAIMessage(currentAIMessageRef.current);
        
        // Update the messages array with streaming content
        setMessages(prev => {
          // Find the streaming message or "Typing..." message
          const streamingIndex = prev.findIndex(msg => 
            msg.role === 'AI' && (msg.content === 'Typing...' || msg.isStreaming === true)
          );
          
          if (streamingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[streamingIndex] = {
              ...updatedMessages[streamingIndex],
              content: currentAIMessageRef.current,
              isStreaming: true,
              // Preserve existing properties
              thinking: updatedMessages[streamingIndex].thinking,
              showThinking: updatedMessages[streamingIndex].showThinking,
              // Add unique ID to prevent React key conflicts
              id: updatedMessages[streamingIndex].id || `streaming_${Date.now()}`
            };
            
            return updatedMessages;
          }
          
          return prev;
        });
        break;

      case 'audio_chunk':
        // Disable audio playback completely
        console.log('Audio playback disabled. Skipping audio chunk.');
        break;
        
      case 'audio_done':
        console.log('Audio streaming complete. Final content:', currentAIMessageRef.current);
        
        // Finalize the streaming message
        setMessages(prev => {
          const streamingIndex = prev.findIndex(msg => 
            msg.role === 'AI' && msg.isStreaming === true
          );
          
          if (streamingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[streamingIndex] = {
              ...updatedMessages[streamingIndex],
              content: currentAIMessageRef.current,
              isStreaming: false,
              id: updatedMessages[streamingIndex].id || `ai_${Date.now()}`
            };
            
            return updatedMessages;
          }
          
          // Fallback: add new message if no streaming message found
          const finalContent = currentAIMessageRef.current.trim();
          if (finalContent) {
            // Remove any remaining "Typing..." messages and add final message
            const withoutTyping = prev.filter(msg => 
              !(msg.role === 'AI' && msg.content === 'Typing...')
            );
            
            return [
              ...withoutTyping,
              {
                role: 'AI',
                content: finalContent,
                isStreaming: false,
                id: `ai_${Date.now()}`
              }
            ];
          }
          
          return prev;
        });

        // Clear buffers
        setCurrentAIMessage('');
        currentAIMessageRef.current = '';
        setIsWaitingForResponse(false);
        
        // Auto-save session after AI response is complete to preserve any confirmation components
        if (saveCurrentSession) {
          console.log('Auto-saving session after AI response completion to preserve confirmation components');
          saveCurrentSession();
        }
        break;

      case 'done':
        console.log('Text streaming complete. Final content:', currentAIMessageRef.current);
        
        // Finalize the streaming message
        setMessages(prev => {
          const streamingIndex = prev.findIndex(msg => 
            msg.role === 'AI' && msg.isStreaming === true
          );
          
          if (streamingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[streamingIndex] = {
              ...updatedMessages[streamingIndex],
              content: currentAIMessageRef.current,
              isStreaming: false,
              id: updatedMessages[streamingIndex].id || `ai_${Date.now()}`
            };
            
            console.log('Finalizing streaming message with content:', currentAIMessageRef.current);
            return updatedMessages;
          }
          
          // Fallback: add new message if no streaming message found
          const finalContent = currentAIMessageRef.current.trim();
          if (finalContent) {
            // Remove any remaining "Typing..." messages and add final message
            const withoutTyping = prev.filter(msg => 
              !(msg.role === 'AI' && msg.content === 'Typing...')
            );
            
            // Check if this content already exists to prevent duplicates
            const contentExists = withoutTyping.some(msg => 
              msg.role === 'AI' && msg.content === finalContent
            );
            
            if (!contentExists) {
              console.log('Adding final AI message:', finalContent);
              return [
                ...withoutTyping,
                {
                  role: 'AI',
                  content: finalContent,
                  isStreaming: false,
                  id: `ai_${Date.now()}`
                }
              ];
            }
          }
          
          return prev;
        });
        
        // Clear buffers
        setCurrentAIMessage('');
        currentAIMessageRef.current = '';
        setIsWaitingForResponse(false);
        break;

      default:
        console.warn('Unknown SSE event type:', event);
    }
  };

  return {
    currentAIMessage,
    isWaitingForResponse,
    setIsWaitingForResponse,
    handleSSEMessage,
    currentAIMessageRef
  };
};

export default useSSEMessageHandler;
